import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Mail, Phone, FileText, ArrowLeft } from 'lucide-react';
import { jobBoardApi, SERVER_URL } from '../../api/jobBoardApi';
import { ApplicantDto, ApplicationStatus } from '../../types/jobBoard';
import Spinner from '../../components/Spinner';

const ApplicantsList = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [applicants, setApplicants] = useState<ApplicantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const statusStyles: Record<ApplicationStatus, string> = {
    Applied: 'bg-blue-50 text-blue-600',
    UnderReview: 'bg-amber-50 text-amber-600',
    InterviewScheduled: 'bg-indigo-50 text-indigo-600',
    Offered: 'bg-purple-50 text-purple-600',
    Rejected: 'bg-rose-50 text-rose-600',
    Hired: 'bg-emerald-50 text-emerald-600',
  };

  const fetchApplicants = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await jobBoardApi.getApplicantsByJob(jobId);
      setApplicants(response.data);
    } catch (error) {
      console.error("Error fetching applicants:", error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  const handleStatusUpdate = async (applicantId: string, newStatus: ApplicationStatus) => {
    setUpdatingId(applicantId);
    try {
      await jobBoardApi.updateApplicantStatus(applicantId, newStatus);
      await fetchApplicants();
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full">
      <header className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/job-board/jobs" className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <Users className="text-indigo-600" size={24} />
              Job Applicants
            </h1>
            <p className="text-slate-500 text-sm mt-1">Managing candidates for job ID: {jobId}</p>
          </div>
        </div>
      </header>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-6 py-4">Full Name</th>
              <th className="px-6 py-4">Contact Details</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Resume</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {applicants.map((app) => (
              <tr key={app.applicantId} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-4 font-bold text-slate-800">{app.fullName}</td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-600 flex flex-col gap-1">
                    <span className="flex items-center gap-1.5"><Mail size={12} /> {app.emailAddress}</span>
                    <span className="flex items-center gap-1.5"><Phone size={12} /> {app.contactNumber}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusStyles[app.applicationStatus] || 'bg-slate-50 text-slate-600'}`}>
                    {app.applicationStatus}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <a href={app.resumePath.startsWith('http') ? app.resumePath : `${SERVER_URL}${app.resumePath}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm font-medium">
                    <FileText size={14} /> View CV
                  </a>
                </td>
                <td className="px-6 py-4 text-right">
                  <select 
                    disabled={updatingId === app.applicantId}
                    value={app.applicationStatus}
                    onChange={(e) => handleStatusUpdate(app.applicantId, e.target.value as ApplicationStatus)}
                    className="text-xs font-semibold bg-slate-50 border border-slate-200 rounded-lg p-1.5 outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer disabled:opacity-50"
                  >
                    {['Applied', 'UnderReview', 'InterviewScheduled', 'Offered', 'Rejected', 'Hired'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {applicants.length === 0 && (
          <div className="p-12 text-center text-slate-400 italic">No applicants found for this position.</div>
        )}
      </div>
    </div>
  );
};

export default ApplicantsList;